import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import PaymentModal from "../components/PaymentModal";
import API_BASE_URL from "../config/api";

// Initialize Stripe
const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY ||
    "pk_test_51RqqTTIUOOIDAzapc2PZR7qsGrgZo5tNPJ4GTpp5GupEh8TgCm6MBIMREv2NRxVBiGJBhuBX8vhB8DPqkiasUaPz00appQjT8O"
);

interface SubscriptionService {
  id: string;
  serviceName: string;
  monthlyLimit: number | null;
  usedThisMonth: number;
  resetDate: string;
  isActive: boolean;
}

interface Subscription {
  id: string;
  planType: "FREE" | "BUSINESS" | "ENTERPRISE";
  status: string;
  billingCycle: "MONTHLY" | "YEARLY";
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  trialStart: string | null;
  trialEnd: string | null;
  services: SubscriptionService[];
  planConfig: {
    apiRequests: number | null;
    tokens: number | null;
    features: string[];
    name: string;
    description: string;
  };
}

// 🆕 NEW: Interface for dynamic plan configurations
interface PlanConfiguration {
  id: string;
  planType: "FREE" | "BUSINESS" | "ENTERPRISE";
  name: string;
  description: string;
  features: string[];
  monthlyPrice: number;
  yearlyPrice: number;
  apiRequestsLimit: number | null;
  tokensLimit: number | null;
  isActive: boolean;
  popularBadge: boolean;
  savings: string | null;
}

const Subscription: React.FC = () => {
  const navigate = useNavigate();

  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [planConfigurations, setPlanConfigurations] = useState<PlanConfiguration[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [billingCycle, setBillingCycle] = useState<"MONTHLY" | "YEARLY">("MONTHLY");
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<"BUSINESS" | "ENTERPRISE" | null>(null);

  useEffect(() => {
    loadSubscription();
    loadPlanConfigurations();
  }, []);

  const loadSubscription = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      if (!token) {
        navigate("/login");
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/subscription`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const data = await response.json();
      if (data.success) {
        setSubscription(data.subscription);
        setBillingCycle(data.subscription.billingCycle || "MONTHLY");
      } else {
        setError(data.message || "Failed to load subscription");
      }
    } catch (error) {
      console.error("Failed to load subscription:", error);
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // 🆕 NEW: Load plan configurations from API
  const loadPlanConfigurations = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE_URL}/api/subscription/pricing`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const data = await response.json();
      if (data.success) {
        setPlanConfigurations(data.data);
      }
    } catch (error) {
      console.error("Failed to load plan configurations:", error);
      // Use fallback if API fails - keep existing functionality
    }
  };

  const handlePlanChange = async (planType: "FREE" | "BUSINESS" | "ENTERPRISE") => {
    if (!subscription || subscription.planType === planType) return;

    if (planType === "FREE") {
      try {
        setActionLoading(true);
        setError("");
        setSuccess("");

        const token = localStorage.getItem("token");
        const response = await fetch(`${API_BASE_URL}/api/subscription`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            planType,
            billingCycle,
          }),
        });

        const data = await response.json();
        if (data.success) {
          setSuccess("Successfully downgraded to FREE plan");
          loadSubscription();
        } else {
          setError(data.message || "Failed to change subscription");
        }
      } catch (error) {
        console.error("Failed to change subscription:", error);
        setError("Network error. Please try again.");
      } finally {
        setActionLoading(false);
      }
    } else {
      setSelectedPlan(planType);
      setShowPaymentModal(true);
    }
  };

  const handleBillingCycleChange = async (newCycle: "MONTHLY" | "YEARLY") => {
    if (
      !subscription ||
      subscription.planType === "FREE" ||
      subscription.billingCycle === newCycle
    )
      return;

    try {
      setActionLoading(true);
      setError("");
      setSuccess("");

      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE_URL}/api/subscription`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          planType: subscription.planType,
          billingCycle: newCycle,
        }),
      });

      const data = await response.json();
      if (data.success) {
        setSuccess(`Billing cycle updated to ${newCycle.toLowerCase()}`);
        setBillingCycle(newCycle);
        loadSubscription();
      } else {
        setError(data.message || "Failed to update billing cycle");
      }
    } catch (error) {
      console.error("Failed to update billing cycle:", error);
      setError("Network error. Please try again.");
    } finally {
      setActionLoading(false);
    }
  };

  const syncSubscriptionStatus = async () => {
    try {
      setActionLoading(true);
      setError("");

      const token = localStorage.getItem("token");
      const response = await fetch(
        `${API_BASE_URL}/api/webhooks/sync-subscription`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const data = await response.json();
      if (data.success) {
        setSubscription(data.subscription);
        setBillingCycle(data.subscription.billingCycle || "MONTHLY");
        setSuccess("Subscription status updated successfully");
      } else {
        setError(data.message || "Failed to sync subscription status");
      }
    } catch (error) {
      console.error("Failed to sync subscription:", error);
      setError("Network error. Please try again.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancelSubscription = async (cancelImmediately: boolean = false) => {
    if (!subscription || subscription.planType === "FREE") return;

    try {
      setActionLoading(true);
      setError("");
      setSuccess("");

      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE_URL}/api/subscription/cancel`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          cancelImmediately,
        }),
      });

      const data = await response.json();
      if (data.success) {
        setSuccess(data.message);
        setShowCancelModal(false);
        loadSubscription();
      } else {
        setError(data.message || "Failed to cancel subscription");
      }
    } catch (error) {
      console.error("Failed to cancel subscription:", error);
      setError("Network error. Please try again.");
    } finally {
      setActionLoading(false);
    }
  };

  const getUsagePercentage = (used: number, limit: number | null): number => {
    if (limit === null) return 0;
    return Math.min((used / limit) * 100, 100);
  };

  // const formatNumber = (num: number | null): string => {
  //   if (num === null) return "Unlimited";
  //   return num.toLocaleString();
  // };
  const formatNumber = (num: number | null): string => {
  // Add this check to prevent the error
  if (num === null || num === undefined) return "Unlimited";
  return num.toLocaleString();
};

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  // 🆕 NEW: Get plan configuration by type
  const getPlanConfig = (planType: string): PlanConfiguration | null => {
    return planConfigurations.find(plan => plan.planType === planType) || null;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading subscription...</p>
        </div>
      </div>
    );
  }

  return (
    <Elements stripe={stripePromise}>
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white shadow">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-6">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">
                  Subscription Management
                </h1>
                <p className="mt-1 text-sm text-gray-500">
                  Manage your subscription plan and usage
                </p>
              </div>
              <div className="flex space-x-4">
                <button
                  onClick={() => navigate("/payment-history")}
                  className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
                >
                  Payment History
                </button>
                <button
                  onClick={() => navigate("/")}
                  className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors"
                >
                  Back to Dashboard
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Messages */}
          {error && (
            <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
              {error}
            </div>
          )}

          {success && (
            <div className="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg">
              {success}
            </div>
          )}

          {/* Current Subscription */}
          {subscription && (
            <div className="bg-white rounded-lg shadow mb-8 p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-gray-900">
                  Current Subscription
                </h2>
                {subscription.status === "INCOMPLETE" && (
                  <button
                    onClick={syncSubscriptionStatus}
                    disabled={actionLoading}
                    className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 transition-colors"
                  >
                    {actionLoading ? "Checking..." : "Refresh Status"}
                  </button>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <p className="text-sm font-medium text-gray-500">Plan</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {subscription.planConfig?.name || subscription.planType || "Unknown Plan"}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Status</p>
                  <p
                    className={`text-lg font-semibold ${
                      subscription.status === "ACTIVE"
                        ? "text-green-600"
                        : subscription.status === "INCOMPLETE"
                        ? "text-yellow-600"
                        : "text-red-600"
                    }`}
                  >
                    {subscription.status || "Unknown"}
                  </p>
                  {subscription.status === "INCOMPLETE" && (
                    <p className="text-xs text-gray-500 mt-1">
                      Payment processing...
                    </p>
                  )}
                </div>
                {subscription.currentPeriodEnd && (
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Next Billing
                    </p>
                    <p className="text-lg font-semibold text-gray-900">
                      {new Date(subscription.currentPeriodEnd).toLocaleDateString()}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="bg-white rounded-lg shadow mb-8 p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-900">
                Recent Payments
              </h2>
              <button
                onClick={() => navigate("/payment-history")}
                className="text-indigo-600 hover:text-indigo-800 font-medium text-sm"
              >
                View All →
              </button>
            </div>
            <div className="text-sm text-gray-600">
              <p>
                View your complete billing history, download invoices, and track
                payment status.
              </p>
            </div>
          </div>

          {/* Usage Statistics */}
          {subscription &&
            subscription.services &&
            subscription.services.length > 0 && (
              <div className="bg-white rounded-lg shadow mb-8 p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  Usage This Month
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {subscription.services.map((service) => (
                    <div key={service.id} className="border rounded-lg p-4">
                      <div className="flex justify-between items-center mb-2">
                        <h3 className="font-medium text-gray-900">
                          {service.serviceName.replace("_", " ")}
                        </h3>
                        <span className="text-sm text-gray-500">
                          {formatNumber(service.usedThisMonth)} /{" "}
                          {formatNumber(service.monthlyLimit)}
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full ${
                            getUsagePercentage(
                              service.usedThisMonth,
                              service.monthlyLimit
                            ) > 80
                              ? "bg-red-500"
                              : getUsagePercentage(
                                  service.usedThisMonth,
                                  service.monthlyLimit
                                ) > 60
                              ? "bg-yellow-500"
                              : "bg-green-500"
                          }`}
                          style={{
                            width: `${getUsagePercentage(
                              service.usedThisMonth,
                              service.monthlyLimit
                            )}%`,
                          }}
                        ></div>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Resets on{" "}
                        {new Date(service.resetDate).toLocaleDateString()}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

          {/* 🔄 UPDATED: Available Plans - Now uses dynamic configurations */}
          <div className="bg-white rounded-lg shadow mb-8 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">
              Available Plans
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {planConfigurations.map((plan) => (
                <div
                  key={plan.planType}
                  className={`border rounded-lg p-6 ${
                    subscription?.planType === plan.planType
                      ? "border-indigo-500 bg-indigo-50"
                      : "border-gray-200"
                  }`}
                >
                  <div className="text-center mb-4">
                    <div className="flex items-center justify-center mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {plan.name}
                      </h3>
                      {plan.popularBadge && (
                        <span className="ml-2 px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                          Popular
                        </span>
                      )}
                    </div>
                    <div className="mt-2">
                      <span className="text-3xl font-bold text-gray-900">
                        {formatCurrency(
                          billingCycle === "YEARLY" ? plan.yearlyPrice : plan.monthlyPrice
                        )}
                      </span>
                      {plan.planType !== "FREE" && (
                        <span className="text-gray-500">
                          /{billingCycle === "YEARLY" ? "year" : "month"}
                        </span>
                      )}
                    </div>
                    {plan.savings && billingCycle === "YEARLY" && (
                      <div className="text-sm text-green-600 mt-1">{plan.savings}</div>
                    )}
                  </div>

                  <div className="mb-4">
                    <p className="text-sm text-gray-600 mb-2">
                      <strong>API Requests:</strong> {formatNumber(plan.apiRequestsLimit)} per month
                    </p>
                    <p className="text-sm text-gray-600 mb-4">
                      <strong>Tokens:</strong> {formatNumber(plan.tokensLimit)} per month
                    </p>

                    <ul className="text-sm text-gray-600 space-y-1">
                      {plan.features &&
                        plan.features.map((feature, index) => (
                          <li key={index} className="flex items-start">
                            <span className="text-green-500 mr-2">✓</span>
                            {feature}
                          </li>
                        ))}
                    </ul>
                  </div>

                  <button
                    onClick={() =>
                      handlePlanChange(plan.planType as "FREE" | "BUSINESS" | "ENTERPRISE")
                    }
                    disabled={
                      actionLoading || subscription?.planType === plan.planType
                    }
                    className={`w-full py-2 px-4 rounded-lg transition-colors ${
                      subscription?.planType === plan.planType
                        ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                        : plan.planType === "FREE"
                        ? "bg-gray-600 text-white hover:bg-gray-700"
                        : "bg-indigo-600 text-white hover:bg-indigo-700"
                    }`}
                  >
                    {subscription?.planType === plan.planType
                      ? "Current Plan"
                      : plan.planType === "FREE"
                      ? "Downgrade to Free"
                      : "Upgrade to " + plan.name}
                  </button>
                </div>
              ))}
            </div>
            
            {/* 🆕 NEW: Billing cycle toggle */}
            {subscription?.planType !== "FREE" && (
              <div className="mt-6 flex justify-center">
                <div className="bg-gray-100 rounded-lg p-1 flex">
                  <button
                    onClick={() => setBillingCycle("MONTHLY")}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                      billingCycle === "MONTHLY"
                        ? "bg-white text-gray-900 shadow-sm"
                        : "text-gray-600 hover:text-gray-900"
                    }`}
                  >
                    Monthly
                  </button>
                  <button
                    onClick={() => setBillingCycle("YEARLY")}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                      billingCycle === "YEARLY"
                        ? "bg-white text-gray-900 shadow-sm"
                        : "text-gray-600 hover:text-gray-900"
                    }`}
                  >
                    Yearly
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Payment Modal */}
          {showPaymentModal && selectedPlan && (
            <PaymentModal
              planType={selectedPlan}
              billingCycle={billingCycle}
              onClose={() => {
                setShowPaymentModal(false);
                setSelectedPlan(null);
              }}
              onSuccess={() => {
                setShowPaymentModal(false);
                setSelectedPlan(null);
                // Wait a moment for Stripe to process the payment
                setTimeout(() => {
                  loadSubscription();
                  setSuccess(`Successfully upgraded to ${selectedPlan} plan`);
                }, 2000);
              }}
              onError={(errorMessage) => {
                setError(errorMessage);
              }}
            />
          )}

          {/* Cancel Modal */}
          {showCancelModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Cancel Subscription
                </h3>
                <p className="text-gray-600 mb-6">
                  Are you sure you want to cancel your subscription?
                </p>
                <div className="flex space-x-4">
                  <button
                    onClick={() => handleCancelSubscription(false)}
                    disabled={actionLoading}
                    className="flex-1 bg-yellow-600 text-white py-2 px-4 rounded-lg hover:bg-yellow-700 transition-colors"
                  >
                    Cancel at Period End
                  </button>
                  <button
                    onClick={() => handleCancelSubscription(true)}
                    disabled={actionLoading}
                    className="flex-1 bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 transition-colors"
                  >
                    Cancel Immediately
                  </button>
                </div>
                <button
                  onClick={() => setShowCancelModal(false)}
                  className="w-full mt-3 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-400 transition-colors"
                >
                  Keep Subscription
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </Elements>
  );
};

export default Subscription;